import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import MicroSimPlayView from '../../components/microsim/MicroSimPlayView';
import MicroSimReport from '../../components/microsim/MicroSimReport';
import microSimService, { newTurnId } from '../../services/microSimService';
import { useMicroSimPaths } from '../../utils/microSimPaths';
import { showError } from '../../utils/toast';

/**
 * "Preview as a pair": the trainer plays both seats of a micro simulation.
 *
 * Same server engine and redaction as a real run — the trainer sees exactly what each
 * participant would see. The view follows whoever's turn it is; the seat switcher
 * lets the trainer look at the other person's screen at any time. Preview runs are
 * private to the trainer and never appear in reports.
 */
const MicroSimPreviewPage = () => {
  const { slug, runId } = useParams();
  const navigate = useNavigate();
  const { topic } = useMicroSimPaths();
  const topicPath = topic(slug);
  const [seat, setSeat] = useState('A');
  const [state, setState] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (slot) => {
    try {
      const res = await microSimService.previewState(runId, slot);
      setState(res.state);
    } catch (err) {
      showError(err.userMessage || 'Could not load the preview');
      navigate(topicPath);
    }
  }, [runId, topicPath, navigate]);

  useEffect(() => { load(seat); }, [seat, load]);

  // Follow the turn: after each move, show the screen of the person who acts next.
  const follow = (next) => {
    if (next.status === 'in_progress' && next.awaitingSlot && next.awaitingSlot !== seat) {
      setSeat(next.awaitingSlot);
    } else {
      setState(next);
    }
  };

  const choose = async (optionId) => {
    setBusy(true);
    try {
      const res = await microSimService.previewChoose(runId, seat, {
        round: state.round, step: state.step, optionId, clientTurnId: newTurnId(),
      });
      follow(res.state);
    } catch (err) {
      if (err?.data?.state) setState(err.data.state);
      else showError(err.userMessage || 'Could not record that reply');
    } finally {
      setBusy(false);
    }
  };

  // In a preview the trainer is both people, so Continue counts for both seats.
  const continueRound = async () => {
    setBusy(true);
    try {
      await microSimService.previewContinue(runId, 'A', state.round);
      const res = await microSimService.previewContinue(runId, 'B', state.round);
      follow(res.state);
    } catch (err) {
      showError(err.userMessage || 'Could not continue');
    } finally {
      setBusy(false);
    }
  };

  const viewReport = async () => {
    try {
      const res = await microSimService.previewReport(runId, seat);
      setReport(res.report);
    } catch (err) {
      showError(err.userMessage || 'The report is not ready yet');
    }
  };

  const seatSwitcher = state && (
    <div className="mb-4 flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2 ring-1 ring-inset ring-amber-200">
      <span className="text-xs font-semibold text-amber-800">Preview — you are playing both people</span>
      <div className="flex gap-1">
        {['A', 'B'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setReport(null); setSeat(s); }}
            className={`rounded-lg px-2.5 py-1 text-xs font-bold ${seat === s ? 'bg-amber-600 text-white' : 'bg-white text-amber-800 ring-1 ring-amber-200'}`}
          >
            Person {s}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout title="Micro simulation preview">
      <div className="mx-auto w-full max-w-3xl px-2 py-4 sm:px-4">
        <button
          type="button"
          onClick={() => navigate(topicPath)}
          className="mb-2 inline-flex items-center gap-1 px-4 text-sm text-neutral-500 hover:text-neutral-800"
        >
          <ArrowLeft size={15} /> Back to the topic
        </button>
        <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200">
          {!state ? (
            <div className="flex justify-center py-24">
              <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-neutral-200 border-t-primary-500" />
            </div>
          ) : report ? (
            <>
              <div className="px-4 pt-4">{seatSwitcher}</div>
              <MicroSimReport report={report} />
            </>
          ) : (
            <MicroSimPlayView
              state={state}
              onChoose={choose}
              onContinue={continueRound}
              onViewReport={viewReport}
              busy={busy}
              topSlot={seatSwitcher}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MicroSimPreviewPage;
